import React, { useEffect, useMemo, useRef, useState } from 'react';
import { formatProjectDateOnly } from 'src/utils/date';

export type ISODate = string;

export type WbsRow = {
    id: string; // ✅ DB nodeId(UUID)
    parentId: string | null; // ✅ DB parentNodeId(UUID|null)
    level: number;
    code?: string;
    name?: string;
    name_ko?: string;
    plannedStart?: ISODate | null;
    plannedFinish?: ISODate | null;
    plannedValue?: number | null;
    displayEarnedValue?: number | null;
    earnedValue?: number | null; // 0..100 (%)
    isWorkPackage?: boolean;
    atRisk?: boolean;
    remarks?: string;
};

/* ---------------- 세션 스토리지에 설정값 저장을 위한 유틸리티 ---------------- */
const SS_PREFIX = 'wbs-gantt';

function ssKey(projectId: string, k: string) {
    return `${SS_PREFIX}:${projectId}:${k}`;
}

function ssGet<T>(key: string, fallback: T): T {
    try {
        const raw = sessionStorage.getItem(key);
        if (!raw) return fallback;
        return JSON.parse(raw) as T;
    } catch {
        return fallback;
    }
}

function ssSet<T>(key: string, value: T) {
    try {
        sessionStorage.setItem(key, JSON.stringify(value));
    } catch {
        // ignore
    }
}

/* ---------------- utilities ---------------- */
const MS_DAY = 24 * 60 * 60 * 1000;
const clamp01 = (x: number) => Math.max(0, Math.min(1, x));
const ev01 = (v?: number | null) => clamp01(((v ?? 0) as number) / 100);
const pctDisplay = (v?: number | null) => Math.round((v ?? 0) as number);
const addDays = (d: Date, days: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + days);
const diffDaysIncl = (a: Date, b: Date) => Math.max(0, Math.round((b.getTime() - a.getTime()) / MS_DAY) + 1); // inclusive

function startOfWeek(d: Date, weekStartsOn = 1) {
    const dd = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const day = dd.getDay();
    const diff = day < weekStartsOn ? day + 7 - weekStartsOn : day - weekStartsOn;
    dd.setDate(dd.getDate() - diff);
    dd.setHours(0, 0, 0, 0);
    return dd;
}
function endOfWeek(d: Date, weekStartsOn = 1) {
    const s = startOfWeek(d, weekStartsOn);
    s.setDate(s.getDate() + 6);
    s.setHours(23, 59, 59, 999);
    return s;
}
function startOfMonth(d: Date) {
    return new Date(d.getFullYear(), d.getMonth(), 1);
}
function endOfMonth(d: Date) {
    return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
}
function fmtYYYYMM(d: Date) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/* ---------------- component ---------------- */
export default function WbsGanttChart({
    projectId,
    rows = [],
    zone = 'Asia/Seoul',
    anchorDate,
    isPM = false,
}: {
    projectId: string;
    rows?: WbsRow[];
    zone?: string;
    anchorDate?: string;
    isPM?: boolean;
}) {
    type Scale = 'week' | 'month';

    const parseISO = (s?: ISODate | null): Date | null => {
        if (!s) return null;
        const ymd = formatProjectDateOnly(s, zone, '');
        if (!ymd) return null;

        const [y, m, d] = ymd.split('-').map(Number);
        return new Date(y, (m ?? 1) - 1, d ?? 1);
    };

    const [scale, setScale] = useState<Scale>(() => ssGet(ssKey(projectId, 'scale'), 'month'));
    const [zoom, setZoom] = useState(() => ssGet(ssKey(projectId, 'zoom'), 1)); // 0.5 ~ 3
    const [leftWidth, setLeftWidth] = useState(() => ssGet(ssKey(projectId, 'leftWidth'), 360));

    const statusWidth = 128;
    const [showRemarks, setShowRemarks] = useState(() => ssGet(ssKey(projectId, 'showRemarks'), true));
    const remarksWidth = 240;
    const [rowH, setRowH] = useState(() => ssGet(ssKey(projectId, 'rowH'), 40));
    const [showContour, setShowContour] = useState(() => ssGet(ssKey(projectId, 'showContour'), false));
    const [showTicks, setShowTicks] = useState(() => ssGet(ssKey(projectId, 'showTicks'), false));
    const [showPctLabel, setShowPctLabel] = useState(() => ssGet(ssKey(projectId, 'showPctLabel'), true));

    // ✅ 날짜 컬럼 토글
    const [showDates, setShowDates] = useState(() => ssGet(ssKey(projectId, 'showDates'), true));

    // ✅ expanded는 Set이라 배열로 저장/복원
    const [expanded, setExpanded] = useState<Set<string>>(() => {
        const arr = ssGet<string[]>(ssKey(projectId, 'expanded'), []);
        return new Set(arr);
    });

    // ✅ 날짜를 수정할 수 있으면 넓게, 그렇지 않으면 좁게
    const dateWidth = isPM ? 140 : 100; // px

    // 로컬 상태에 데이터 보관(인라인 편집 반영)
    const [data, setData] = useState<WbsRow[]>(rows);

    // ✅ 세션스토리지에서 값 읽어오기
    useEffect(() => {
        ssSet(ssKey(projectId, 'scale'), scale);
    }, [projectId, scale]);

    useEffect(() => {
        ssSet(ssKey(projectId, 'zoom'), zoom);
    }, [projectId, zoom]);

    useEffect(() => {
        ssSet(ssKey(projectId, 'leftWidth'), leftWidth);
    }, [projectId, leftWidth]);

    useEffect(() => {
        ssSet(ssKey(projectId, 'showRemarks'), showRemarks);
    }, [projectId, showRemarks]);

    useEffect(() => {
        ssSet(ssKey(projectId, 'rowH'), rowH);
    }, [projectId, rowH]);

    useEffect(() => {
        ssSet(ssKey(projectId, 'showContour'), showContour);
    }, [projectId, showContour]);

    useEffect(() => {
        ssSet(ssKey(projectId, 'showTicks'), showTicks);
    }, [projectId, showTicks]);

    useEffect(() => {
        ssSet(ssKey(projectId, 'showPctLabel'), showPctLabel);
    }, [projectId, showPctLabel]);

    useEffect(() => {
        ssSet(ssKey(projectId, 'showDates'), showDates);
    }, [projectId, showDates]);

    useEffect(() => {
        ssSet(ssKey(projectId, 'expanded'), Array.from(expanded));
    }, [projectId, expanded]);

    useEffect(() => {
        setData(Array.isArray(rows) ? rows : []);
    }, [rows]);

    const childMap = useMemo(() => {
        const m = new Map<string | null, WbsRow[]>();
        for (const r of data) {
            const k = r.parentId ?? null;
            if (!m.has(k)) m.set(k, []);
            m.get(k)!.push(r);
        }
        for (const [, arr] of m) {
            arr.sort(
                (a, b) =>
                    (a.code || '').localeCompare(b.code || '') ||
                    (a.name_ko || a.name || '').localeCompare(b.name_ko || b.name || ''),
            );
        }
        return m;
    }, [data]);
    const roots = childMap.get(null) || [];

    const toggle = (id: string) =>
        setExpanded((prev) => {
            const n = new Set(prev);
            n.has(id) ? n.delete(id) : n.add(id);
            return n;
        });

    // ✅ 전체 펼치기 / 전체 접기 토글(타이틀 옆)
    const expandableIds = useMemo(() => {
        const ids: string[] = [];
        for (const r of data) {
            const kids = childMap.get(r.id) || [];
            if (kids.length > 0) ids.push(r.id);
        }
        return ids;
    }, [data, childMap]);

    const allExpanded = expandableIds.length > 0 && expanded.size === expandableIds.length;

    const toggleAll = () => {
        setExpanded((prev) => {
            if (expandableIds.length === 0) return prev;
            if (prev.size === expandableIds.length) return new Set(); // 전체 접기
            return new Set(expandableIds); // 전체 펼치기
        });
    };

    const visible = useMemo(() => {
        const out: WbsRow[] = [];
        const dfs = (nodes: WbsRow[], open = true) => {
            for (const n of nodes) {
                if (n.level === 1 || open) {
                    out.push(n);
                    const kids = childMap.get(n.id) || [];
                    if (kids.length && expanded.has(n.id)) dfs(kids, true);
                }
            }
        };
        dfs(roots, true);
        return out;
    }, [roots, childMap, expanded]);

    // ✅ 인라인 업데이트 & (옵션) 서버 저장
    function updateRow(id: string, patch: Partial<WbsRow>) {
        setData((cur) => cur.map((r) => (r.id === id ? { ...r, ...patch } : r)));
        // (옵션) 서버 저장 예시: onBlur 시 호출 권장
        // saveSchedule(id, patch.plannedStart, patch.plannedFinish).catch(console.error);
    }

    async function saveSchedule(id: string, plannedStart?: string | null, plannedFinish?: string | null) {
        // 백엔드 준비되면 주석 해제해서 사용
        // await fetch(`/api/wbs/${id}/schedule`, {
        //   method: "PATCH",
        //   headers: { "Content-Type": "application/json" },
        //   body: JSON.stringify({ plannedStart, plannedFinish }),
        // });
    }

    // 타임라인 범위 계산은 data 기준
    type Segment = { start: Date; end: Date; header: string };
    const segments = useMemo<Segment[]>(() => {
        let min: Date | null = null,
            max: Date | null = null;
        for (const r of data) {
            const s = parseISO(r.plannedStart);
            const e = parseISO(r.plannedFinish);
            if (s && (!min || s < min)) min = s;
            if (e && (!max || e > max)) max = e;
        }
        const anchor = parseISO(anchorDate ?? null) ?? new Date();
        if (!min) min = addDays(anchor, -14);
        if (!max) max = addDays(anchor, 70);

        const vs = scale === 'week' ? startOfWeek(min) : startOfMonth(min);
        const ve = scale === 'week' ? endOfWeek(max) : endOfMonth(max);

        const arr: Segment[] = [];
        if (scale === 'week') {
            for (let cur = startOfWeek(vs); cur <= ve; cur = addDays(cur, 7)) {
                arr.push({ start: startOfWeek(cur), end: endOfWeek(cur), header: '' });
            }
        } else {
            for (let cur = startOfMonth(vs); cur <= ve; cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1)) {
                arr.push({ start: startOfMonth(cur), end: endOfMonth(cur), header: fmtYYYYMM(cur) });
            }
        }
        return arr;
    }, [data, scale, anchorDate]);

    const segmentCount = segments.length;
    const MIN_COL_PX = 80;
    const colMinPx = Math.max(1, Math.round(MIN_COL_PX * zoom));
    const gridMinTotalPx = segmentCount * colMinPx;

    // 스크롤 동기화
    const headerScrollRef = useRef<HTMLDivElement>(null);
    const bodyScrollRef = useRef<HTMLDivElement>(null);
    const syncingRef = useRef<'header' | 'body' | null>(null);
    const onHeaderScroll = (e: React.UIEvent<HTMLDivElement>) => {
        if (syncingRef.current === 'body') return;
        syncingRef.current = 'header';
        if (bodyScrollRef.current) bodyScrollRef.current.scrollLeft = (e.target as HTMLDivElement).scrollLeft;
        syncingRef.current = null;
    };
    const onBodyScroll = (e: React.UIEvent<HTMLDivElement>) => {
        if (syncingRef.current === 'header') return;
        syncingRef.current = 'body';
        if (headerScrollRef.current) headerScrollRef.current.scrollLeft = (e.target as HTMLDivElement).scrollLeft;
        syncingRef.current = null;
    };

    // 좌측 폭 드래그
    const dragRef = useRef<{ startX: number; startW: number } | null>(null);
    const onDragStart = (e: React.MouseEvent) => {
        dragRef.current = { startX: e.clientX, startW: leftWidth };
        window.addEventListener('mousemove', onDragMove);
        window.addEventListener('mouseup', onDragEnd);
    };
    const onDragMove = (e: MouseEvent) => {
        if (!dragRef.current) return;
        const dx = e.clientX - dragRef.current.startX;
        const next = Math.max(240, Math.min(640, dragRef.current.startW + dx));
        setLeftWidth(next);
    };
    const onDragEnd = () => {
        dragRef.current = null;
        window.removeEventListener('mousemove', onDragMove);
        window.removeEventListener('mouseup', onDragEnd);
    };

    // 위치/막대 계산
    const xPct = (d: Date) => {
        if (segmentCount === 0) return 0;
        const lastEndPlus = addDays(segments[segmentCount - 1].end, 1);
        if (d <= segments[0].start) return 0;
        if (d >= lastEndPlus) return 100;

        for (let i = 0; i < segmentCount; i++) {
            const s = segments[i];
            const segEndPlus = addDays(s.end, 1);
            if (d < segEndPlus) {
                const segDays = diffDaysIncl(s.start, s.end);
                const elapsed = Math.max(0, Math.min(segDays, Math.round((d.getTime() - s.start.getTime()) / MS_DAY)));
                const ratioInSeg = clamp01(elapsed / segDays);
                return ((i + ratioInSeg) / segmentCount) * 100;
            }
        }
        return 100;
    };

    function plannedBarPct(r: WbsRow) {
        const s = parseISO(r.plannedStart);
        const e = parseISO(r.plannedFinish);
        if (!s || !e || e < s || segmentCount === 0) return { leftPct: -1, widthPct: 0 };
        const leftPct = +xPct(s).toFixed(4);
        const rightPct = +xPct(addDays(e, 1)).toFixed(4); // inclusive
        const widthPct = Math.max(0, rightPct - leftPct);
        return { leftPct, widthPct };
    }
    function progressBarPct(r: WbsRow, plan = plannedBarPct(r)) {
        const widthPct = Math.min(plan.widthPct * ev01(r.displayEarnedValue ?? r.earnedValue), plan.widthPct);
        const endPct = plan.leftPct + widthPct;
        return { leftPct: plan.leftPct, widthPct: +widthPct.toFixed(4), endPct: +endPct.toFixed(4) };
    }

    const anchorPct = useMemo(() => {
        const anchor = parseISO(anchorDate ?? null);
        if (!anchor) return 0;
        return xPct(anchor);
    }, [anchorDate, segments]);

    type Chip = 'ontrack' | 'delay' | 'risk';
    const chipClass: Record<Chip, string> = {
        ontrack: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20',
        delay: 'bg-amber-50  text-amber-700  ring-1 ring-amber-600/20',
        risk: 'bg-rose-50   text-rose-700   ring-1 ring-rose-600/20',
    };
    const chipLabel: Record<Chip, string> = { ontrack: 'On track', delay: 'Delay', risk: 'Risk' };
    function chipsForRow(r: WbsRow): Chip[] {
        const ev = pctDisplay(r.displayEarnedValue ?? r.earnedValue);
        const pv = pctDisplay(r.plannedValue);
        const chips: Chip[] = [];
        chips.push(ev >= pv ? 'ontrack' : 'delay');
        if (r.atRisk || ev + 10 < pv) chips.push('risk');
        return chips;
    }

    // 공통 열 정의 (✅ 날짜 2열 조건부 삽입)
    const headerCols =
        `${leftWidth}px` +
        (showDates ? ` ${dateWidth}px ${dateWidth}px` : '') +
        ` 1fr ${statusWidth}px` +
        (showRemarks ? ` ${remarksWidth}px` : '');
    const bodyCols = headerCols;

    const timelineGridStyle: React.CSSProperties = {
        gridTemplateColumns: `repeat(${segmentCount}, minmax(${colMinPx}px, 1fr))`,
    };

    // 두 막대 공통 스타일(위치/두께 동일)
    const barH = Math.round(rowH * 0.5);
    const barTop = Math.round((rowH - barH) / 2);
    const barBase = 'absolute box-border rounded-lg border //shadow-lg';

    return (
        <div className="w-full min-h-screen //p-6 overflow-x-hidden">
            {/* Toolbar */}
            <div className="mb-3 flex flex-wrap items-center gap-2">
                {/* ✅ 타이틀 구성 유지 + 옆에 토글만 추가 */}
                <div className="flex items-center gap-2 mr-auto">
                    {/* <h1 className="text-lg font-semibold">WBS Gantt Chart</h1> */}
                    {/* <button
                        type="button"
                        onClick={toggleAll}
                        disabled={expandableIds.length === 0}
                        className="ml-6 text-xs rounded border px-2 py-1 hover:bg-gray-50 disabled:opacity-50"
                        title={allExpanded ? '전체 접기' : '전체 펼치기'}
                    >
                        {allExpanded ? '전체 접기' : '전체 펼치기'}
                    </button> */}
                </div>

                <button
                    type="button"
                    onClick={toggleAll}
                    disabled={expandableIds.length === 0}
                    className="mr-3 text-xs rounded border px-2 py-1 hover:bg-gray-50 disabled:opacity-50"
                    title={allExpanded ? '전체 접기' : '전체 펼치기'}
                >
                    {allExpanded ? '전체 접기' : '전체 펼치기'}
                </button>

                {/* Scale */}
                <div className="inline-flex items-center gap-1 text-xs">
                    <div className="h-6 w-px bg-gray-300 mx-1" />

                    <span className="text-gray-500">Scale</span>
                    <button
                        className={`rounded border px-2 py-1 hover:bg-gray-50 ${scale === 'week' ? 'bg-gray-100' : ''}`}
                        onClick={() => setScale('week')}
                    >
                        Week
                    </button>
                    <button
                        className={`rounded border px-2 py-1 hover:bg-gray-50 ${
                            scale === 'month' ? 'bg-gray-100' : ''
                        }`}
                        onClick={() => setScale('month')}
                    >
                        Month
                    </button>
                </div>

                {/* Zoom */}
                <div className="h-6 w-px bg-gray-300 mx-1" />
                <div className="inline-flex items-center gap-1 text-xs">
                    <span className="text-gray-500">Zoom</span>
                    <button
                        className="rounded border px-2 py-1 hover:bg-gray-50"
                        onClick={() => setZoom((v) => Math.max(0.25, +(v - 0.1).toFixed(1)))}
                    >
                        -
                    </button>
                    <span className="w-12 text-center">{(zoom * 100).toFixed(0)}%</span>
                    <button
                        className="rounded border px-2 py-1 hover:bg-gray-50"
                        onClick={() => setZoom((v) => Math.min(3, +(v + 0.1).toFixed(1)))}
                    >
                        +
                    </button>
                </div>

                {/* Row height */}
                <div className="h-6 w-px bg-gray-300 mx-1" />
                <div className="inline-flex items-center gap-1 text-xs">
                    <span className="text-gray-500">Row</span>
                    <button
                        className="rounded border px-2 py-1 hover:bg-gray-50"
                        onClick={() => setRowH((v) => Math.max(28, v - 4))}
                    >
                        -
                    </button>
                    <span className="w-10 text-center">{rowH}px</span>
                    <button
                        className="rounded border px-2 py-1 hover:bg-gray-50"
                        onClick={() => setRowH((v) => Math.min(56, v + 4))}
                    >
                        +
                    </button>
                </div>

                {/* Toggles */}
                <div className="h-6 w-px bg-gray-300 mx-1" />
                <label className="text-xs inline-flex items-center gap-1">
                    <input
                        type="checkbox"
                        className="accent-gray-700"
                        checked={showRemarks}
                        onChange={(e) => setShowRemarks(e.target.checked)}
                    />
                    Show remarks
                </label>
                {/* ✅ 날짜 토글 */}
                <label className="text-xs inline-flex items-center gap-1">
                    <input
                        type="checkbox"
                        className="accent-gray-700"
                        checked={showDates}
                        onChange={(e) => setShowDates(e.target.checked)}
                    />
                    Show dates
                </label>
                {/* ✅ 날짜 토글 */}
                <label className="text-xs inline-flex items-center gap-1">
                    <input
                        type="checkbox"
                        className="accent-gray-700"
                        checked={showPctLabel}
                        onChange={(e) => setShowPctLabel(e.target.checked)}
                    />
                    Show EV/PV label
                </label>
            </div>

            {/* Frame */}
            <div className="rounded-2xl border bg-white overflow-hidden text-sm">
                {/* Headers */}
                <div className="grid" style={{ gridTemplateColumns: headerCols }}>
                    <div className="px-3 py-3 border-b //bg-gray-50 text-xs text-gray-600 font-medium sticky top-0 z-10">
                        <div className="flex items-center //justify-between gap-6">
                            <span>작업 (WBS)</span>
                            {/* <button
                                type="button"
                                onClick={toggleAll}
                                disabled={expandableIds.length === 0}
                                className="text-[11px] rounded border px-2 py-0.5 hover:bg-white disabled:opacity-50"
                                title={allExpanded ? '전체 접기' : '전체 펼치기'}
                            >
                                {allExpanded ? '전체 접기' : '전체 펼치기'}
                            </button> */}
                        </div>
                    </div>

                    {/* ✅ 날짜 헤더 2칸 */}
                    {showDates && (
                        <>
                            <div className="px-3 py-2 border-b //bg-gray-50 text-xs text-gray-600 font-medium sticky top-0 z-10">
                                Start
                            </div>
                            <div className="px-3 py-2 border-b //bg-gray-50 text-xs text-gray-600 font-medium sticky top-0 z-10">
                                Finish
                            </div>
                        </>
                    )}

                    {/* Timeline header */}
                    <div className="border-b sticky top-0 z-10 bg-white min-w-0 relative">
                        {/* 드래그로 좌측폭 조절 */}
                        <div
                            className="absolute left-[-4px] top-0 bottom-0 w-1.5 cursor-col-resize bg-transparent z-20"
                            onMouseDown={onDragStart}
                            title="드래그로 좌측 폭 조절"
                        >
                            <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-[3px] bg-gray-200/80 hover:bg-gray-300/80 rounded" />
                        </div>
                        <div ref={headerScrollRef} className="overflow-x-auto" onScroll={onHeaderScroll}>
                            <div style={{ minWidth: gridMinTotalPx, width: '100%' }}>
                                {scale === 'week' ? (
                                    <MonthBlocksByWeeks segments={segments} />
                                ) : (
                                    <div
                                        className="grid text-xs font-medium text-gray-600 select-none"
                                        style={timelineGridStyle}
                                    >
                                        {segments.map((s, i) => (
                                            <div
                                                key={i}
                                                className="px-2 py-1 border-r last:border-r-0 //bg-gray-50 text-center"
                                            >
                                                {fmtYYYYMM(s.start)}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {scale === 'week' && (
                                    <div
                                        className="grid text-[10px] text-gray-500 select-none"
                                        style={timelineGridStyle}
                                    >
                                        {segments.map((_, i) => (
                                            <div
                                                key={i}
                                                className="h-6 border-r last:border-r-0 flex items-center justify-center"
                                            >
                                                W{i + 1}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="px-3 py-2 border-b //bg-gray-50 text-xs text-gray-600 font-medium sticky top-0 z-10">
                        Status
                    </div>
                    {showRemarks && (
                        <div className="px-3 py-2 border-b //bg-gray-50 text-xs text-gray-600 font-medium sticky top-0 z-10">
                            비고
                        </div>
                    )}
                </div>

                {/* Body */}
                <div className="overflow-y-auto" style={{ height: '80vh' }}>
                    <div className="grid" style={{ gridTemplateColumns: bodyCols }}>
                        {/* WBS tree */}
                        <div className="border-r">
                            {visible.map((r) => {
                                const kids = childMap.get(r.id) || [];
                                const canExpand = kids.length > 0;
                                const isOpen = expanded.has(r.id);
                                const name = r.name_ko || r.name || r.code || `#${r.id}`;
                                return (
                                    <div key={r.id} className="px-2 border-b" style={{ height: rowH }}>
                                        <div
                                            className="flex items-center gap-1 h-full"
                                            style={{ paddingLeft: (r.level - 1) * 14 }}
                                        >
                                            {canExpand ? (
                                                <button
                                                    onClick={() => toggle(r.id)}
                                                    className="w-5 h-5 inline-flex items-center justify-center rounded hover:bg-gray-100 focus:outline-none"
                                                    aria-label={isOpen ? 'Collapse' : 'Expand'}
                                                >
                                                    <svg
                                                        width="10"
                                                        height="10"
                                                        viewBox="0 0 20 20"
                                                        className={`transition-transform ${isOpen ? 'rotate-90' : ''}`}
                                                    >
                                                        <path d="M6 4l8 6-8 6V4z" fill="currentColor" />
                                                    </svg>
                                                </button>
                                            ) : (
                                                <span className="w-5 h-5 inline-block" />
                                            )}
                                            <div className="min-w-0">
                                                <div className="truncate text-sm font-medium leading-tight">{name}</div>
                                                <div className="text-[9px] text-gray-500 leading-tight">
                                                    {r.code || ''}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* ✅ Start / Finish inputs */}
                        {showDates && (
                            <>
                                {/* Start */}
                                <div className="border-r">
                                    {visible.map((r) => {
                                        const v = data.find((d) => d.id === r.id)!;
                                        const start = v.plannedStart ?? '';
                                        const finish = v.plannedFinish ?? '';
                                        const invalid = !!start && !!finish && finish < start;
                                        return (
                                            <div
                                                key={r.id}
                                                className="px-2 border-b flex items-center"
                                                style={{ height: rowH }}
                                            >
                                                {isPM ? (
                                                    <input
                                                        type="date"
                                                        className={`w-full rounded border px-2 py-1 text-xs ${
                                                            invalid ? 'border-rose-400 bg-rose-50' : 'border-gray-300'
                                                        }`}
                                                        value={start}
                                                        onChange={(e) =>
                                                            updateRow(r.id, {
                                                                plannedStart: e.target.value || null,
                                                            })
                                                        }
                                                        onBlur={() => saveSchedule(r.id, start || null, finish || null)}
                                                    />
                                                ) : (
                                                    <div
                                                        className="w-full text-xs text-gray-700 tabular-nums px-1 truncate"
                                                        title={start || '-'}
                                                    >
                                                        {start || '-'}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                                {/* Finish */}
                                <div className="border-r">
                                    {visible.map((r) => {
                                        const v = data.find((d) => d.id === r.id)!;
                                        const start = v.plannedStart ?? '';
                                        const finish = v.plannedFinish ?? '';
                                        const invalid = !!start && !!finish && finish < start;
                                        return (
                                            <div
                                                key={r.id}
                                                className="px-2 border-b flex items-center"
                                                style={{ height: rowH }}
                                            >
                                                {isPM ? (
                                                    <input
                                                        type="date"
                                                        className={`w-full rounded border px-2 py-1 text-xs ${
                                                            invalid ? 'border-rose-400 bg-rose-50' : 'border-gray-300'
                                                        }`}
                                                        value={finish}
                                                        onChange={(e) =>
                                                            updateRow(r.id, {
                                                                plannedFinish: e.target.value || null,
                                                            })
                                                        }
                                                        onBlur={() => saveSchedule(r.id, start || null, finish || null)}
                                                    />
                                                ) : (
                                                    <div
                                                        className="w-full text-xs text-gray-700 tabular-nums px-1 truncate"
                                                        title={finish || '-'}
                                                    >
                                                        {finish || '-'}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}

                        {/* Timeline */}
                        <div className="relative border-r min-w-0">
                            {/* 드래그로 좌측 폭 조절 */}
                            {/* <div
                                className="absolute left-[-4px] top-0 bottom-0 w-1.5 cursor-col-resize bg-transparent"
                                onMouseDown={onDragStart}
                                title="드래그로 좌측 폭 조절"
                            >
                                <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-[3px] bg-gray-200/80 hover:bg-gray-300/80 rounded" />
                            </div> */}
                            <div ref={bodyScrollRef} className="overflow-x-auto relative" onScroll={onBodyScroll}>
                                <div className="relative" style={{ minWidth: gridMinTotalPx, width: '100%' }}>
                                    {/* vertical grid */}
                                    <div
                                        className="absolute inset-0 pointer-events-none grid"
                                        style={timelineGridStyle}
                                    >
                                        {segments.map((_, i) => (
                                            <div key={i} className="border-r last:border-r-0" />
                                        ))}
                                    </div>

                                    {/* Current anchor guide */}
                                    <div
                                        className="absolute top-0 bottom-0 border-r-2 border-rose-500/40 pointer-events-none"
                                        style={{ left: `${anchorPct}%` }}
                                    />

                                    {/* rows */}
                                    <div>
                                        {visible.map((r) => {
                                            const plan = plannedBarPct(r);
                                            const prog = progressBarPct(r, plan);
                                            const hasPlan = plan.widthPct > 0 && plan.leftPct >= 0;
                                            const hasProg = prog.widthPct > 0 && prog.leftPct >= 0;

                                            const ev = pctDisplay(r.displayEarnedValue ?? r.earnedValue);
                                            const pv = pctDisplay(r.plannedValue);

                                            const plannedColor = r.isWorkPackage
                                                ? 'bg-emerald-200 border-emerald-400'
                                                : 'bg-blue-200 border-blue-400';
                                            const progressColor = r.isWorkPackage
                                                ? 'bg-emerald-600 border-emerald-700'
                                                : 'bg-blue-600 border-blue-700';

                                            return (
                                                <div key={r.id} className="border-b relative" style={{ height: rowH }}>
                                                    {hasPlan && (
                                                        <div
                                                            className={`${barBase} ${plannedColor}`}
                                                            style={{
                                                                left: `${plan.leftPct}%`,
                                                                width: `${plan.widthPct}%`,
                                                                top: barTop,
                                                                height: barH,
                                                            }}
                                                            title={`${r.code || ''} ${r.name_ko || r.name || ''}\n계획: ${
                                                                r.plannedStart ?? ''
                                                            } ~ ${r.plannedFinish ?? ''}`}
                                                        />
                                                    )}
                                                    {hasProg && (
                                                        <div
                                                            className={`${barBase} ${progressColor}`}
                                                            style={{
                                                                left: `${plan.leftPct}%`,
                                                                width: `${prog.widthPct}%`,
                                                                top: barTop,
                                                                height: barH,
                                                            }}
                                                            title={`EV ${pctDisplay(r.displayEarnedValue ?? r.earnedValue)}%`}
                                                        />
                                                    )}
                                                    {showPctLabel && hasProg && (
                                                        <div
                                                            className="absolute"
                                                            style={{
                                                                top: barTop,
                                                                left: `calc(${Math.min(98, prog.endPct)}% + 4px)`,
                                                                height: barH,
                                                                pointerEvents: 'none',
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                            title={`EV/PV = ${ev}% / ${pv}%`}
                                                        >
                                                            <div className="h-full flex items-center">
                                                                <span className="text-[11px] leading-none px-1 py-0.5 rounded border bg-white/85 backdrop-blur-[1px] text-gray-700">
                                                                    {ev}%/{pv}%
                                                                </span>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {showTicks && hasProg && (
                                                        <div
                                                            className="absolute w-0.5 bg-gray-700/70"
                                                            style={{
                                                                left: `${prog.endPct}%`,
                                                                top: rowH * 0.2,
                                                                height: rowH * 0.6,
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        })}

                                        {/* contour */}
                                        {showContour && visible.length > 0 && (
                                            <svg
                                                className="pointer-events-none absolute left-0 top-0 w-full"
                                                height={visible.length * rowH}
                                                viewBox={`0 0 100 ${visible.length * rowH}`}
                                                preserveAspectRatio="none"
                                            >
                                                <polyline
                                                    points={visible
                                                        .map((r, i) => {
                                                            const plan = plannedBarPct(r);
                                                            const prog = progressBarPct(r, plan);
                                                            const x = prog.widthPct > 0 ? prog.endPct : plan.leftPct;
                                                            const y = i * rowH + rowH / 2;
                                                            return `${x},${y}`;
                                                        })
                                                        .join(' ')}
                                                    fill="none"
                                                    stroke="rgba(244,63,94,0.45)"
                                                    strokeWidth={2}
                                                />
                                            </svg>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Status */}
                        <div>
                            {visible.map((r) => {
                                const chips = chipsForRow(r);
                                return (
                                    <div key={r.id} className="px-3 border-b" style={{ height: rowH }}>
                                        <div className="h-full flex items-center gap-1 flex-wrap">
                                            {chips.map((c) => (
                                                <span
                                                    key={c}
                                                    className={`px-2 py-0.5 text-[11px] rounded-md ${chipClass[c]}`}
                                                >
                                                    {chipLabel[c]}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Remarks */}
                        {showRemarks && (
                            <div>
                                {visible.map((r) => (
                                    <div
                                        key={r.id}
                                        className="px-3 border-b text-sm text-gray-700"
                                        style={{ height: rowH }}
                                    >
                                        <div className="h-full flex items-center truncate">{r.remarks || '-'}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Legend */}
            <div className="mt-3 text-[11px] text-gray-500 flex items-center gap-4">
                <span>
                    <span className="inline-block w-3 h-3 rounded bg-blue-200 border border-blue-400 align-middle mr-1" />{' '}
                    Summary
                </span>
                <span>
                    <span className="inline-block w-3 h-3 rounded bg-emerald-200 border border-emerald-400 align-middle mr-1" />{' '}
                    Work Package
                </span>
                <span>
                    <span className="inline-block w-4 h-1.5 rounded bg-blue-600 align-middle mr-1" />/
                    <span className="inline-block w-4 h-1.5 rounded bg-emerald-600 align-middle mr-1" /> Progress (EV%)
                </span>
                <span className="text-rose-600">연한 빨간선</span>: 모든 행 EV 끝점 컨투어
                <span className="text-gray-700">세로 틱</span>: 각 행 EV 끝점
            </div>
        </div>
    );
}

/* --------- Month blocks for weekly scale --------- */
function MonthBlocksByWeeks({ segments }: { segments: { start: Date }[] }) {
    if (!segments.length) return null;
    const groups: { label: string; count: number }[] = [];
    let cur = fmtYYYYMM(segments[0].start);
    let cnt = 0;
    for (const s of segments) {
        const key = fmtYYYYMM(s.start);
        if (key !== cur) {
            groups.push({ label: cur, count: cnt });
            cur = key;
            cnt = 1;
        } else {
            cnt += 1;
        }
    }
    groups.push({ label: cur, count: cnt });

    const total = segments.length || 1;
    return (
        <div className="flex text-[11px] text-gray-600 select-none">
            {groups.map((g, i) => (
                <div
                    key={`${g.label}-${i}`}
                    className="px-2 py-1 border-r last:border-r-0 //bg-gray-50 text-center"
                    style={{ width: `${(g.count / total) * 100}%` }}
                >
                    {g.label}
                </div>
            ))}
        </div>
    );
}
